import React, { useId } from 'react';

// Building blocks shared by the scenes. Coordinates are stage units (360×400);
// `data-*` attributes are the hooks the GSAP timelines select.
const place = (x, y) => ({ left: `${x / 3.6}%`, top: `${y / 4}%` });

export function Anchor({ x, y, edge, className = '', children, ...props }) {
  return <div className={`studio-feature-art__anchor ${edge ? `studio-feature-art__anchor--${edge}` : ''} ${className}`} style={place(x, y)} {...props}>{children}</div>;
}

export function Glow({ x, y, tone, size }) {
  return <span className={`studio-feature-art__glow is-${tone}`} data-glow style={{ ...place(x, y), ...(size ? { width: `${size / 3.6}cqw` } : null) }} />;
}

export function Pill({ id, tone = 'cool', icon: Icon, children, className = '' }) {
  return <span className={`studio-feature-art__pill is-${tone} ${className}`} data-pill={id} data-enter>
    {Icon && <Icon aria-hidden="true" />}{children}
    <span className="studio-feature-art__ring" data-ring={id} />
  </span>;
}

export function Core({ icon: Icon, className = '' }) {
  return <div className={`studio-feature-art__core ${className}`} data-core>
    <span className="studio-feature-art__core-halo" data-core-halo />
    <span className="studio-feature-art__core-rim"><span className="studio-feature-art__core-sweep" data-core-sweep /></span>
    <span className="studio-feature-art__core-bevel"><span className="studio-feature-art__core-face" data-core-face><Icon aria-hidden="true" /></span></span>
  </div>;
}

export function Beam({ x, height }) {
  return <span className="studio-feature-art__beam" data-beam style={{ left: `${x / 3.6}%`, height: `${height / 4}%` }} />;
}

export function Sparks({ points }) {
  return points.map(([x, y], i) => <span key={i} className="studio-feature-art__spark" data-spark style={place(x, y)} />);
}

// `wires`: [{ id, tone, d, joints: [[x, y]], dashed }]. Each wire gets a base
// line, a streak + head for travelling light, and glowing joints at its ends.
export function Wires({ wires }) {
  const glow = `fx-glow-${useId().replace(/:/g, '')}`;
  return <svg className="studio-feature-art__wires" viewBox="0 0 360 400" aria-hidden="true" focusable="false">
    <defs>
      <filter id={glow} x="-50%" y="-50%" width="200%" height="200%">
        <feGaussianBlur stdDeviation="2.2" result="blur" />
        <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
      </filter>
    </defs>
    {wires.map(wire => <g key={wire.id} className={`is-${wire.tone || 'cool'}`}>
      <path className={`studio-feature-art__wire ${wire.dashed ? 'studio-feature-art__wire--dashed' : ''}`} d={wire.d} pathLength="1" data-wire={wire.id} />
      {!wire.dashed && <>
        <path className="studio-feature-art__streak" d={wire.d} pathLength="1" data-streak={wire.id} filter={`url(#${glow})`} />
        <circle className="studio-feature-art__head" r="3" data-head={wire.id} filter={`url(#${glow})`} />
      </>}
      {(wire.joints || []).map(([x, y], i) => <circle key={i} className="studio-feature-art__joint" cx={x} cy={y} r="2.3" data-joint={`${wire.id}-${i}`} filter={`url(#${glow})`} />)}
    </g>)}
  </svg>;
}
